import { useEffect, useState } from 'react';
import Select from 'react-select';
import { useGraph } from '../../../../../js/components/graph/GraphProvider';
import { MAP_ELEMENT_TYPES, UI_ELEMENTS } from '../../../../constants/graph';
import translations from '../../../../translations';
import type { MapElement, Outcome } from '../../../../types/graph';
import {
    getAutoAssignmentType,
    applyApprovalOutcomeConstraints,
    updateApprovalOutcomeAssignment,
} from '../../../../utils/approval';
import { isNullOrEmpty } from '../../../../utils/guard';
import { getSharedStyles } from '../../../../utils/select';
import { stringReplace } from '../../../../utils/string';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import FormGroup from '../../../generic/FormGroup';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import ConfigModal from '../../../graph/ConfigModal';

interface Props {
    dismissMapElementConfig: () => void;
    container: HTMLElement;
    sourceMapElementId: string;
}

interface UseGraph {
    mapElements: [MapElement];
    refreshFlow: () => void;
    saveMapElement: (mapElement: MapElement) => Promise<void>;
}

const CreateNewOutcome = ({ dismissMapElementConfig, container, sourceMapElementId }: Props) => {
    const { mapElements, refreshFlow, saveMapElement }: UseGraph = useGraph();

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [sourceMapElement, setSourceMapElement] = useState<MapElement>();
    const [targetMapElement, setTargetMapElement] = useState<MapElement>();
    const [otherMapElements, setOtherMapElements] = useState<MapElement[]>([]);
    const [newOutcome, setNewOutcome] = useState<Outcome>({
        attributes: {},
        className: null,
        comparison: null,
        controlPoints: null,
        developerName: '',
        developerSummary: null,
        flowOut: null,
        id: crypto.randomUUID(),
        isBulkAction: false,
        label: null,
        nextMapElementDeveloperName: null,
        nextMapElementId: null,
        order: 0,
        pageActionBindingType: 'SAVE',
        pageActionType: null,
        pageObjectBindingId: null,
    });

    const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
        // The modal appears after the DOM is loaded so we need to wait in order to set a menuPortalTarget
        requestAnimationFrame(() => {
            setMenuPortalTarget(document.querySelector('.outcome-modal') as HTMLElement);
        });
    }, []);

    useEffect(() => {
        const sourceEle = mapElements.find((ele) => ele.id === sourceMapElementId);

        if (!isNullOrEmpty(sourceEle)) {
            setOtherMapElements(
                mapElements.filter((ele) => {
                    if (ele.id !== sourceEle?.id) {
                        return true;
                    }

                    return false;
                }),
            );
            setSourceMapElement(sourceEle);
        }
    }, [mapElements, sourceMapElementId]);

    // Separate useEffect to handle approval element pre-filling
    useEffect(() => {
        if (sourceMapElement) {
            const isApprovalElement = sourceMapElement.elementType === MAP_ELEMENT_TYPES.approval;
            if (isApprovalElement) {
                const autoAssignType = getAutoAssignmentType(sourceMapElement);
                if (autoAssignType) {
                    // Derive fixed name/label/order using shared util
                    setNewOutcome((prev) => ({
                        ...prev,
                        ...applyApprovalOutcomeConstraints(prev, autoAssignType),
                    }));
                }
            }
        }
    }, [sourceMapElement]);

    const message = stringReplace(
        translations.OUTCOME_CREATE_message,
        sourceMapElement?.developerName ?? '',
    );

    const labelRequired = UI_ELEMENTS.includes(sourceMapElement?.elementType ?? '');

    // Check if this is an approval element and determine the outcome type
    const isApprovalElement = sourceMapElement?.elementType === MAP_ELEMENT_TYPES.approval;
    const autoAssignType = isApprovalElement ? getAutoAssignmentType(sourceMapElement) : null;
    const isApprovalOutcome = isApprovalElement && autoAssignType !== null;
    const isApprove = autoAssignType === 'approve';

    const isTargetElementValid = !isNullOrEmpty(targetMapElement);
    const isNameValid = !isNullOrEmpty(newOutcome.developerName);
    const isLabelValid = !(isNullOrEmpty(newOutcome.label) && labelRequired);
    const isFormValid = isTargetElementValid && isNameValid && isLabelValid;

    const onSave = async () => {
        setHasSubmitted(true);

        if (isFormValid && sourceMapElement) {
            // Check if this is an approval element and apply constraints
            const isApprovalElement = sourceMapElement.elementType === MAP_ELEMENT_TYPES.approval;
            let outcomeToAdd = { ...newOutcome };
            let autoAssignType: 'approve' | 'deny' | null = null;

            if (isApprovalElement) {
                autoAssignType = getAutoAssignmentType(sourceMapElement);
                if (autoAssignType) {
                    // Apply approval constraints (name, label, order)
                    outcomeToAdd = {
                        ...outcomeToAdd,
                        ...applyApprovalOutcomeConstraints(outcomeToAdd, autoAssignType),
                    };
                }
            }

            const updatedOutcomes: Outcome[] = sourceMapElement.outcomes || [];
            updatedOutcomes.push(outcomeToAdd);

            let newMapElement: MapElement = {
                ...sourceMapElement,
                outcomes: updatedOutcomes,
            };

            // Update approval object with outcome ID if this is an approval element
            // Use the previously determined autoAssignType to avoid re-evaluating after the outcome was added
            if (isApprovalElement && autoAssignType) {
                newMapElement = updateApprovalOutcomeAssignment(
                    newMapElement,
                    outcomeToAdd.id,
                    autoAssignType,
                );
            }

            await saveMapElement(newMapElement);
            dismissMapElementConfig();
        }
    };

    const onCancel = () => {
        refreshFlow();
        dismissMapElementConfig();
    };

    const onSetTargetElement = (elementId: string) => {
        const selectedElement = otherMapElements.find((ele) => ele.id === elementId);

        if (isNullOrEmpty(selectedElement)) {
            setTargetMapElement(undefined);
            setNewOutcome({ ...newOutcome, nextMapElementId: null });
        } else {
            setTargetMapElement(selectedElement);
            const name = selectedElement?.developerName
                ? `Go to ${selectedElement?.developerName}`
                : 'Go';
            setNewOutcome({
                ...newOutcome,
                nextMapElementId: selectedElement?.id ?? null,
                // For approval outcomes, preserve the fixed name/label (Approve/Deny)
                ...(isApprovalOutcome
                    ? {}
                    : {
                          developerName: name,
                          label: name,
                      }),
            });
        }
    };

    const getElementValue = () => {
        if (targetMapElement) {
            return { value: targetMapElement.id, label: targetMapElement.developerName };
        }

        return null;
    };

    const renderBody = () => (
        <>
            {isApprovalOutcome && (
                <div className="alert alert-info approval-outcome-alert">
                    {isApprove
                        ? translations.APPROVAL_fixed_outcome_label_approve
                        : translations.APPROVAL_fixed_outcome_label_deny}
                </div>
            )}
            <p>{message}</p>
            <div className="margin-top">
                <FormGroup
                    label={translations.OUTCOME_CREATE_element_select_label}
                    htmlFor="add-outcome-select"
                    isRequired
                    validationMessage={translations.OUTCOME_CREATE_element_select_validation}
                    isValid={isTargetElementValid}
                    showValidation={hasSubmitted}
                >
                    <Select
                        inputId="add-outcome-select"
                        className="form-control-width"
                        value={getElementValue()}
                        onChange={(selection) => onSetTargetElement(selection?.value ?? '')}
                        options={otherMapElements.map((ele) => ({
                            label: ele.developerName,
                            value: ele.id,
                        }))}
                        placeholder={translations.OUTCOME_CREATE_element_select_placeholder}
                        menuPosition="fixed"
                        menuPortalTarget={menuPortalTarget}
                        closeMenuOnScroll={(e) => {
                            if (e.target === document.getElementsByClassName('modal-body')[0]) {
                                return true;
                            }
                            return false;
                        }}
                        styles={getSharedStyles<{
                            label: string | null | undefined;
                            value: string;
                        }>()}
                    />
                </FormGroup>
                <FormGroup
                    label={translations.OUTCOME_CREATE_name_label}
                    htmlFor="add-outcome-name"
                    isRequired
                    validationMessage={translations.OUTCOME_CREATE_name_validation}
                    isValid={isNameValid}
                    showValidation={hasSubmitted}
                >
                    <input
                        id="add-outcome-name"
                        className="form-control form-control-width"
                        value={newOutcome?.developerName}
                        onChange={
                            isApprovalOutcome
                                ? () => {}
                                : (e) =>
                                      setNewOutcome({
                                          ...newOutcome,
                                          developerName: e.target.value,
                                      })
                        }
                        disabled={isApprovalOutcome}
                    />
                </FormGroup>
                {labelRequired ? (
                    <FormGroup
                        label={translations.OUTCOME_CREATE_label_label}
                        htmlFor="add-outcome-label"
                        isRequired
                        validationMessage={translations.OUTCOME_CREATE_label_validation}
                        isValid={isLabelValid}
                        showValidation={hasSubmitted}
                    >
                        <input
                            id="add-outcome-label"
                            className="form-control form-control-width"
                            value={newOutcome?.label ?? ''}
                            onChange={(e) =>
                                setNewOutcome({
                                    ...newOutcome,
                                    label: e.target.value,
                                })
                            }
                        />
                    </FormGroup>
                ) : null}
            </div>
        </>
    );

    const renderFooter = () => (
        <>
            <ButtonDefault className="flex-child-right" onClick={onCancel}>
                {translations.GRAPH_config_panel_cancel}
            </ButtonDefault>
            <ButtonPrimary className="margin-left" onClick={onSave}>
                {translations.GRAPH_config_panel_save}
            </ButtonPrimary>
        </>
    );
    return (
        <ConfigModal
            id={sourceMapElement?.id ?? ''}
            title={translations.OUTCOME_CREATE_heading}
            elementType={MAP_ELEMENT_TYPES.outcome}
            onHide={onCancel}
            container={container}
        >
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </ConfigModal>
    );
};

export default CreateNewOutcome;
